import { useNavigate } from "react-router-dom";
import DataTable, { Column } from "../../components/DataTable";
import Tag from "../../components/Tags";
import { copyIcon, edit, link, playIcon, trash } from "../../asset";
import {
  formatDateTime,
  popupNavigation,
} from "../../util/functions/UniversalFunctions";
import { TooltipWrapper } from "../../components";
import { Exam } from "../../types/ExamType";
import useConfirmToast from "../../util/hooks/useConfirmToast";
import examsApis from "../../api/ExamsApis";
import { useState } from "react";
import ShareLinksPopup from "../../components/modal/ShareLinksPopup";
import { useBranchCode } from "../../util/hooks/useBranch";

interface ExamsListProps {
  list: Exam[];
  loading: boolean;
  refreshList: () => void;
}

export default function ExamsList({
  list,
  loading,
  refreshList,
}: ExamsListProps) {
  const navigate = useNavigate();
  const { confirm, modal } = useConfirmToast();
  const [showShareModal, setShowShareModal] = useState<string | null>(null);
  const branchCode = useBranchCode()

  const deleteExam = async (examId: number) => {
    const result = await confirm("Are you sure you want to delete this Exam?");
    if (!result) return;
    await examsApis.deleteExamForm(examId.toString());
    if (refreshList) refreshList();
  };

  const columns: Column<Exam>[] = [
    {
      label: "SL NO.",
      accessor: "examSeqId",
      searchable: false,
    },
    {
      label: "Exam Name",
      accessor: "examName",
      searchable: true,
      render: (item) => (
        <div className="text-neutral-400 text-sm font-bold">
          {item?.examName}
        </div>
      ),
    },
    {
      label: "For Class",
      accessor: "forClass",
      searchOptions: ["Nursery A", "Nursery B", "Nursery C", "Nursery D"],
      searchable: true,
      searchType: "select",
      render: (item) => (
        <p className="max-w-44 line-clamp-2 text-[13px]">{/* {item.forClass} */} --</p>
      ),
    },
    {
      label: "Created On",
      accessor: "date",
      searchable: true,
      searchType: "date",
      render: (item) => (
        <div className="max-w-24 text-[13px]">{formatDateTime(item?.createdAt)}</div>
      ),
    },
    {
      label: "Created By",
      accessor: "createdBy",
      render: (item) => (
        <div className="text-[13px]">
          {item.createdBy}
        </div>
      ),
    },
    {
      label: "View Results",
      accessor: "result",
      render: (item) => (
        <div className="w-full">
          <Tag color="success" text="View Results" />
        </div>
      ),
    },
    {
      label: "Link",
      accessor: "link",
      render: (item) => {
        const examLink = `${window.location.origin}/exams-and-assignments/${branchCode}/edit-customExam/${item.examSeqId}`;

        return (
          <button
            onClick={() => setShowShareModal(examLink)}
            className="flex items-center gap-2"
          >
            <img src={link} alt="link" />
          </button>
        );
      },
    },
    {
      label: "Action",
      accessor: "action",
      render: (item) => (
        <div className="flex gap-6">
          <TooltipWrapper label="Setup">
            <button
              onClick={() =>
                navigate(
                  `/exams-and-assignments/create-exam/test/${item.examId}`,
                  {
                    state: {
                      examSeqId: item.examSeqId,
                    },
                  }
                )
              }
            >
              <img src={playIcon} alt="play" />
            </button>
          </TooltipWrapper>

          <TooltipWrapper label="Duplicate">
            <button>
              <img src={copyIcon} alt="duplicate" />
            </button>
          </TooltipWrapper>

          <TooltipWrapper label="Edit">
            <button
              onClick={() =>
                navigate(`/exams-and-assignments/create-exam/${item.examId}`, {
                  state: {
                    examSeqId: item.examSeqId,
                  },
                })
              }
            >
              <img src={edit} alt="edit" className="w-[18px] h-[18px]" />
            </button>
          </TooltipWrapper>

          <TooltipWrapper label="Delete">
            <button onClick={() => deleteExam(item.examId)}>
              <img src={trash} alt="delete" className="w-[18px] h-[18px]" />
            </button>
          </TooltipWrapper>
        </div>
      ),
    },
  ];

  return (
    <div className="grid gap-7 bg-white px-6 py-5 rounded-lg">
      <DataTable columns={columns} data={list ?? []} isLoading={loading} />
      {modal}
      {showShareModal && (
        <ShareLinksPopup
          title="Exam Link"
          link={showShareModal}
          onClose={() => setShowShareModal(null)}
        />
      )}
    </div>
  );
}
